/**
 * Payroll calendar and compliance rules. Pay dates come from each pay schedule; filing deadlines
 * come from versioned rules that cite their source and review date (review every year in Q4, when
 * agencies publish next year's rates and calendars). A due date on a weekend or federal holiday
 * moves to the next business day.
 */
import type { StateHolidays } from "./compliance";
import { federalHolidays, nextBusinessDay } from "./due-dates";

export type PayFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly";
export type PaySchedule = { frequency: PayFrequency; anchorPayDate: string; daysOfMonth: number[]; cutoffBusinessDays: number };

const iso = (d: Date) => d.toISOString().slice(0, 10);
const at = (s: string) => new Date(`${s}T12:00:00Z`);
const addDays = (s: string, n: number) => iso(new Date(at(s).getTime() + n * 86_400_000));
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const isBusinessDay = (s: string) => {
  const w = at(s).getUTCDay();
  // Banking days: federal holidays, but not DC's Emancipation Day. Semiweekly deposits could count it
  // as a legal holiday (Publication 15); leaving it out keeps those dates a day early, never late.
  return w !== 0 && w !== 6 && !federalHolidays(Number(s.slice(0, 4)), { irs: false }).has(s);
};
/** A banking day for pay dates: banks are open on Friday Dec 31 when New Year's Day is a Saturday. */
const isBankingDay = (s: string) => {
  const w = at(s).getUTCDay();
  return w !== 0 && w !== 6 && !federalHolidays(Number(s.slice(0, 4)), { irs: false, banking: true }).has(s);
};
/** Pay dates and cutoffs use banking days: a Friday payday before a Saturday New Year's Day stays. */
function previousBankingDay(s: string): string {
  let d = s;
  while (!isBankingDay(d)) d = addDays(d, -1);
  return d;
}
function bankingDaysBefore(s: string, n: number): string {
  let d = s;
  for (let i = 0; i < n; i++) {
    d = addDays(d, -1);
    while (!isBankingDay(d)) d = addDays(d, -1);
  }
  return d;
}
/** The business day on or before a date (payroll runs early when a pay date falls on a holiday). */
export function previousBusinessDay(s: string): string {
  let d = s;
  while (!isBusinessDay(d)) d = addDays(d, -1);
  return d;
}
export function businessDaysBefore(s: string, n: number): string {
  let d = s;
  for (let i = 0; i < n; i++) {
    d = addDays(d, -1);
    while (!isBusinessDay(d)) d = addDays(d, -1);
  }
  return d;
}
export function addBusinessDays(s: string, n: number): string {
  let d = s;
  for (let i = 0; i < n; i++) {
    d = addDays(d, 1);
    while (!isBusinessDay(d)) d = addDays(d, 1);
  }
  return d;
}

/** Pay dates in [from, to], each moved to the business day before when it falls on a weekend or holiday. */
export function payDates(s: PaySchedule, from: string, to: string): Array<{ payDate: string; cutoffOn: string }> {
  const out: string[] = [];
  if (s.frequency === "weekly" || s.frequency === "biweekly") {
    const step = s.frequency === "weekly" ? 7 : 14;
    const diff = Math.round((at(from).getTime() - at(s.anchorPayDate).getTime()) / 86_400_000);
    let d = addDays(s.anchorPayDate, Math.ceil(diff / step) * step);
    while (d <= addDays(to, 3)) {
      out.push(d);
      d = addDays(d, step);
    }
  } else {
    const days = s.frequency === "monthly" ? [s.daysOfMonth[0] ?? 31] : s.daysOfMonth.length ? s.daysOfMonth : [15, 31];
    let y = Number(from.slice(0, 4));
    let m = Number(from.slice(5, 7)) - 1;
    for (let i = 0; i < 400; i++) {
      for (const day of days) out.push(`${y}-${String(m).padStart(2, "0")}-${String(Math.min(day, lastDay(y, m))).padStart(2, "0")}`.replace(/-00-/, "-12-"));
      if (`${y}-${String(m).padStart(2, "0")}` > to.slice(0, 7)) break;
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
  }
  return [...new Set(out.map(previousBankingDay))]
    .filter((d) => d >= from && d <= to)
    .sort()
    .map((payDate) => ({ payDate, cutoffOn: bankingDaysBefore(payDate, s.cutoffBusinessDays) }));
}

// --- New hires --------------------------------------------------------------------------------

export type HireState = "MD" | "VA" | "DC" | "other";
const STATE_CERT: Record<HireState, string | null> = { MD: "Form MW507 (Maryland withholding)", VA: "Form VA-4 (Virginia withholding)", DC: "Form D-4 (DC withholding)", other: null };

/** What a new hire needs, with the legal deadlines counted from the hire date. */
export function newHireChecklist(hireDate: string, state: HireState): Array<{ key: string; label: string; dueOn: string | null }> {
  const items = [
    { key: "w4", label: "Form W-4 (federal withholding)", dueOn: hireDate },
    ...(STATE_CERT[state] ? [{ key: "state_w4", label: STATE_CERT[state]!, dueOn: hireDate }] : []),
    { key: "i9_1", label: "Form I-9, Section 1 (employee, by the first day of work)", dueOn: hireDate },
    { key: "i9_2", label: "Form I-9, Section 2 (employer reviews documents within 3 business days)", dueOn: addBusinessDays(hireDate, 3) },
    { key: "new_hire_report", label: `New-hire report to ${state === "other" ? "the state" : state === "DC" ? "DC" : state === "MD" ? "Maryland" : "Virginia"} (within 20 days)`, dueOn: addDays(hireDate, 20) },
    { key: "direct_deposit", label: "Direct deposit authorization", dueOn: null },
    { key: "added_to_payroll", label: "Added to payroll before the first pay date", dueOn: null },
  ];
  return items;
}

// --- Filings ----------------------------------------------------------------------------------

export type PayrollProfileRules = {
  states: string[];
  federalDepositSchedule: "monthly" | "semiweekly";
  files944: boolean;
  mdWithholdingFrequency: "accelerated" | "monthly" | "quarterly" | "annual";
  vaWithholdingFrequency: "semiweekly" | "monthly" | "quarterly";
  dcWithholdingFrequency: "monthly" | "quarterly" | "annual";
  employeeCount: number;
  hasContractors: boolean;
};

type Due =
  | { type: "quarterly"; monthsAfter: number; day: number | "last" }
  | { type: "monthly"; monthsAfter: number; day: number | "last"; skipQuarterEnd?: boolean }
  | { type: "annual"; month: number; day: number | "last" };

export type PayrollRule = {
  key: string;
  form: string;
  title: string;
  agency: string;
  jurisdiction: "federal" | "MD" | "VA" | "DC";
  due: Due;
  /** First tax year (or quarter, as YYYY-Qn) the rule applies to. */
  startsWith?: string;
  /** The agency keeps the date even on a weekend (DC DOES reports). */
  noWeekendShift?: boolean;
  /** Due after each pay date rather than on a calendar (generated from the pay calendar). */
  perPayDate?: "semiweekly" | "three_business_days" | "va_semiweekly";
  /** Generated from the amounts recorded on processed payrolls (nextDayDeposits), not from a calendar. */
  fromLiabilities?: true;
  applies: (p: PayrollProfileRules) => boolean;
  note?: string;
  source: string;
};

const has = (p: PayrollProfileRules, st: string) => p.states.includes(st);

export const PAYROLL_RULES: PayrollRule[] = [
  { key: "941", form: "Form 941", title: "Employer's quarterly federal tax return", agency: "IRS", jurisdiction: "federal", due: { type: "quarterly", monthsAfter: 1, day: "last" }, applies: (p) => !p.files944, source: "IRS: Instructions for Form 941" },
  { key: "944", form: "Form 944", title: "Employer's annual federal tax return", agency: "IRS", jurisdiction: "federal", due: { type: "annual", month: 1, day: 31 }, applies: (p) => p.files944, source: "IRS: Instructions for Form 944" },
  {
    key: "fed_deposit",
    form: "Federal tax deposit",
    title: "Monthly deposit of withheld and employer taxes (EFTPS)",
    agency: "IRS",
    jurisdiction: "federal",
    due: { type: "monthly", monthsAfter: 1, day: 15 },
    // Form 944 filers deposit too (on their schedule) unless the year's taxes are under $2,500.
    applies: (p) => p.federalDepositSchedule === "monthly",
    note: "Monthly depositors: $50,000 or less of taxes in the lookback period. A quarter under $2,500 (this quarter or the last; for Form 944 filers, the year) may be paid with the return instead. $100,000 or more on any day is due the next business day.",
    source: "IRS Publication 15, section 11",
  },
  {
    key: "fed_deposit_sw",
    form: "Federal tax deposit",
    title: "Semiweekly deposit for a pay date (EFTPS)",
    agency: "IRS",
    jurisdiction: "federal",
    due: { type: "monthly", monthsAfter: 0, day: 1 },
    perPayDate: "semiweekly",
    applies: (p) => p.federalDepositSchedule === "semiweekly",
    note: "Paid Wednesday–Friday: deposit by the next Wednesday; paid Saturday–Tuesday: by the next Friday.",
    source: "IRS Publication 15, section 11",
  },
  {
    key: "fed_deposit_nextday",
    form: "Next-day deposit",
    title: "Federal tax deposit: $100,000 or more accumulated (EFTPS)",
    agency: "IRS",
    jurisdiction: "federal",
    due: { type: "monthly", monthsAfter: 0, day: 1 },
    fromLiabilities: true,
    applies: () => true,
    note: "Federal taxes on the payrolls of one deposit period reached $100,000: deposit by the next business day. The employer is a semiweekly depositor from the next day for the rest of this year and all of next year.",
    source: "IRS Publication 15, section 11 ($100,000 Next-Day Deposit Rule)",
  },
  {
    key: "940",
    form: "Form 940",
    title: "Annual federal unemployment (FUTA) tax return",
    agency: "IRS",
    jurisdiction: "federal",
    due: { type: "annual", month: 1, day: 31 },
    applies: () => true,
    note: "February 10 if all FUTA tax was deposited on time. FUTA is deposited quarterly once the amount owed passes $500.",
    source: "IRS: Instructions for Form 940",
  },
  {
    key: "futa_deposit",
    form: "FUTA deposit",
    title: "Federal unemployment (FUTA) tax deposit (EFTPS)",
    agency: "IRS",
    jurisdiction: "federal",
    due: { type: "quarterly", monthsAfter: 1, day: "last" },
    // Added in October 2026: earlier quarters aren't listed (they'd all show as overdue on the first
    // day); the amounts carried from them still count toward the $500.
    startsWith: "2026-Q3",
    applies: () => true,
    note: "Deposit by this date if the FUTA tax not yet deposited this year is more than $500; $500 or less carries to the next quarter. A fourth-quarter balance of $500 or less is paid with Form 940. When every payroll in the year has its FUTA amount recorded, ClientFlow works this out.",
    source: "IRS: Instructions for Form 940 (When must you deposit your FUTA tax?)",
  },
  { key: "w2", form: "Forms W-2 and W-3", title: "Wage statements to employees and the Social Security Administration", agency: "SSA", jurisdiction: "federal", due: { type: "annual", month: 1, day: 31 }, applies: () => true, source: "IRS: General Instructions for Forms W-2 and W-3" },
  { key: "1099nec", form: "Form 1099-NEC", title: "Nonemployee compensation to contractors and the IRS", agency: "IRS", jurisdiction: "federal", due: { type: "annual", month: 1, day: 31 }, applies: (p) => p.hasContractors, source: "IRS: Instructions for Forms 1099-MISC and 1099-NEC" },
  // Maryland
  {
    key: "mw506_acc",
    form: "Form MW506",
    title: "Maryland withholding (accelerated: after each pay date)",
    agency: "Comptroller of Maryland",
    jurisdiction: "MD",
    due: { type: "monthly", monthsAfter: 0, day: 1 },
    perPayDate: "three_business_days",
    applies: (p) => has(p, "MD") && p.mdWithholdingFrequency === "accelerated",
    note: "For employers that withheld $15,000 or more last year and $700 or more in the pay period.",
    source: "Comptroller of Maryland: Employer Withholding Guide",
  },
  { key: "mw506_m", form: "Form MW506", title: "Maryland withholding return (monthly)", agency: "Comptroller of Maryland", jurisdiction: "MD", due: { type: "monthly", monthsAfter: 1, day: 15 }, applies: (p) => has(p, "MD") && p.mdWithholdingFrequency === "monthly", source: "Comptroller of Maryland: Employer Withholding Guide" },
  { key: "mw506_q", form: "Form MW506", title: "Maryland withholding return (quarterly)", agency: "Comptroller of Maryland", jurisdiction: "MD", due: { type: "quarterly", monthsAfter: 1, day: 15 }, applies: (p) => has(p, "MD") && p.mdWithholdingFrequency === "quarterly", source: "Comptroller of Maryland: Employer Withholding Guide" },
  { key: "mw506_a", form: "Form MW506", title: "Maryland withholding return (annual)", agency: "Comptroller of Maryland", jurisdiction: "MD", due: { type: "annual", month: 1, day: 31 }, applies: (p) => has(p, "MD") && p.mdWithholdingFrequency === "annual", source: "Comptroller of Maryland: Employer Withholding Guide" },
  { key: "mw508", form: "Form MW508", title: "Maryland annual withholding reconciliation (with W-2s)", agency: "Comptroller of Maryland", jurisdiction: "MD", due: { type: "annual", month: 1, day: 31 }, applies: (p) => has(p, "MD"), source: "Comptroller of Maryland: Form MW508 instructions" },
  { key: "md_ui", form: "Contribution and employment report", title: "Maryland unemployment insurance quarterly report", agency: "Maryland Department of Labor", jurisdiction: "MD", due: { type: "quarterly", monthsAfter: 1, day: "last" }, applies: (p) => has(p, "MD"), source: "Maryland Division of Unemployment Insurance: employer reporting" },
  {
    key: "md_famli",
    form: "FAMLI contribution report",
    title: "Maryland FAMLI contributions (quarterly)",
    agency: "Maryland Department of Labor (FAMLI)",
    jurisdiction: "MD",
    due: { type: "quarterly", monthsAfter: 1, day: "last" },
    startsWith: "2027-Q1",
    applies: (p) => has(p, "MD"),
    note: "From January 1, 2027: 0.9% of wages up to the Social Security wage base ($192,300 in 2027), split evenly with employees; employers with fewer than 15 employees don't pay the employer half. Later years' rates are announced each November.",
    source: "Maryland Department of Labor: FAMLI employer contributions",
  },
  // Virginia
  { key: "va5_m", form: "Form VA-5", title: "Virginia withholding return (monthly)", agency: "Virginia Tax", jurisdiction: "VA", due: { type: "monthly", monthsAfter: 1, day: 25 }, applies: (p) => has(p, "VA") && p.vaWithholdingFrequency === "monthly", source: "Virginia Tax: Employer Withholding Instructions" },
  { key: "va5_q", form: "Form VA-5", title: "Virginia withholding return (quarterly)", agency: "Virginia Tax", jurisdiction: "VA", due: { type: "quarterly", monthsAfter: 1, day: "last" }, applies: (p) => has(p, "VA") && p.vaWithholdingFrequency === "quarterly", source: "Virginia Tax: Employer Withholding Instructions" },
  {
    key: "va15",
    form: "Form VA-15",
    title: "Virginia semiweekly withholding payment",
    agency: "Virginia Tax",
    jurisdiction: "VA",
    due: { type: "monthly", monthsAfter: 0, day: 1 },
    perPayDate: "va_semiweekly",
    applies: (p) => has(p, "VA") && p.vaWithholdingFrequency === "semiweekly",
    note: "Due within 3 banking days after the federal semiweekly period ends (Tuesday or Friday): the same day as the federal semiweekly deposit. Owed once Virginia withholding since the last payment passes $500.",
    source: "Virginia Tax: Employer Withholding Instructions",
  },
  { key: "va16", form: "Form VA-16", title: "Virginia quarterly reconciliation (semiweekly filers)", agency: "Virginia Tax", jurisdiction: "VA", due: { type: "quarterly", monthsAfter: 1, day: "last" }, applies: (p) => has(p, "VA") && p.vaWithholdingFrequency === "semiweekly", source: "Virginia Tax: Employer Withholding Instructions" },
  { key: "va6", form: "Form VA-6", title: "Virginia annual withholding reconciliation (with W-2s)", agency: "Virginia Tax", jurisdiction: "VA", due: { type: "annual", month: 1, day: 31 }, applies: (p) => has(p, "VA"), source: "Virginia Tax: Employer Withholding Instructions" },
  { key: "va_vec", form: "VEC-FC-20/21", title: "Virginia unemployment quarterly tax and wage report", agency: "Virginia Employment Commission", jurisdiction: "VA", due: { type: "quarterly", monthsAfter: 1, day: "last" }, applies: (p) => has(p, "VA"), source: "Virginia Employment Commission: employer reports" },
  // District of Columbia
  { key: "dc_pay_m", form: "DC withholding payment", title: "DC withholding payment (monthly)", agency: "DC Office of Tax and Revenue", jurisdiction: "DC", due: { type: "monthly", monthsAfter: 1, day: 20 }, applies: (p) => has(p, "DC") && p.dcWithholdingFrequency === "monthly", source: "DC OTR: Employer withholding (MyTax.DC.gov)" },
  { key: "dc_fr900q", form: "Form FR-900Q", title: "DC withholding return (quarterly)", agency: "DC Office of Tax and Revenue", jurisdiction: "DC", due: { type: "quarterly", monthsAfter: 1, day: 20 }, applies: (p) => has(p, "DC") && p.dcWithholdingFrequency !== "annual", source: "DC OTR: Form FR-900Q instructions" },
  { key: "dc_fr900a", form: "Form FR-900A", title: "DC annual withholding return and payment (small employers)", agency: "DC Office of Tax and Revenue", jurisdiction: "DC", due: { type: "annual", month: 1, day: 20 }, applies: (p) => has(p, "DC") && p.dcWithholdingFrequency === "annual", source: "DC OTR: Form FR-900A instructions" },
  { key: "dc_w2", form: "Forms W-2", title: "W-2s to DC (MyTax.DC.gov)", agency: "DC Office of Tax and Revenue", jurisdiction: "DC", due: { type: "annual", month: 1, day: 31 }, applies: (p) => has(p, "DC"), source: "DC OTR: W-2 filing requirements" },
  { key: "dc_ui", form: "Form UC-30", title: "DC unemployment quarterly contribution and wage report", agency: "DC Department of Employment Services", jurisdiction: "DC", due: { type: "quarterly", monthsAfter: 1, day: "last" }, noWeekendShift: true, note: "DOES keeps the date when it falls on a weekend: file before it.", applies: (p) => has(p, "DC"), source: "DC DOES: Employer Handbook" },
  { key: "dc_pfl", form: "Paid Family Leave report", title: "DC Paid Family Leave (0.75% of wages, paid by the employer)", agency: "DC Department of Employment Services", jurisdiction: "DC", due: { type: "quarterly", monthsAfter: 1, day: "last" }, noWeekendShift: true, note: "Filed with the UC-30 dates; file before a weekend due date.", applies: (p) => has(p, "DC"), source: "DC Office of Paid Family Leave" },
];

export const PAYROLL_RULES_REVIEWED = "2026-10-18";

/**
 * Maryland and Virginia dates also skip the state holidays the firm added (Firm Settings › Rules and
 * rates): `holidays` gives the list in effect for a statutory date. Federal and DC dates don't.
 */
const stateExtra = (jurisdiction: PayrollRule["jurisdiction"], statutory: string, holidays?: StateHolidays) => (holidays && (jurisdiction === "MD" || jurisdiction === "VA") ? holidays(jurisdiction, statutory) : null);

function dueDate(due: Due, periodEndYear: number, periodEndMonth: number, shift = true, jurisdiction: PayrollRule["jurisdiction"] = "federal", holidays?: StateHolidays): string {
  const irs = jurisdiction === "federal" || jurisdiction === "DC";
  const nbd = shift ? (x: string) => nextBusinessDay(x, { irs, extraHolidays: stateExtra(jurisdiction, x, holidays) }) : (x: string) => x;
  if (due.type === "annual") {
    const y = periodEndYear + 1;
    return nbd(`${y}-${String(due.month).padStart(2, "0")}-${String(due.day === "last" ? lastDay(y, due.month) : due.day).padStart(2, "0")}`);
  }
  let y = periodEndYear;
  let m = periodEndMonth + due.monthsAfter;
  if (m > 12) {
    m -= 12;
    y += 1;
  }
  return nbd(`${y}-${String(m).padStart(2, "0")}-${String(due.day === "last" ? lastDay(y, m) : due.day).padStart(2, "0")}`);
}

export type FilingDue = { ruleKey: string; period: string; periodLabel: string; dueOn: string };

/** Every filing a payroll client owes with a due date in [from, to]. */
export function filingsDue(p: PayrollProfileRules, from: string, to: string, holidays?: StateHolidays): FilingDue[] {
  const out: FilingDue[] = [];
  const y0 = Number(from.slice(0, 4)) - 1;
  const y1 = Number(to.slice(0, 4));
  for (const r of PAYROLL_RULES) {
    if (!r.applies(p) || r.perPayDate || r.fromLiabilities) continue;
    const shift = !r.noWeekendShift;
    for (let y = y0; y <= y1; y++) {
      if (r.due.type === "annual") {
        if (r.startsWith && String(y) < r.startsWith.slice(0, 4)) continue;
        out.push({ ruleKey: r.key, period: String(y), periodLabel: String(y), dueOn: dueDate(r.due, y, 12, shift, r.jurisdiction, holidays) });
      } else if (r.due.type === "quarterly") {
        for (let q = 1; q <= 4; q++) {
          const period = `${y}-Q${q}`;
          if (r.startsWith && period < r.startsWith) continue;
          out.push({ ruleKey: r.key, period, periodLabel: `Q${q} ${y}`, dueOn: dueDate(r.due, y, q * 3, shift, r.jurisdiction, holidays) });
        }
      } else {
        for (let m = 1; m <= 12; m++) {
          const period = `${y}-${String(m).padStart(2, "0")}`;
          if (r.startsWith && period < r.startsWith) continue;
          out.push({ ruleKey: r.key, period, periodLabel: new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }), dueOn: dueDate(r.due, y, m, shift, r.jurisdiction, holidays) });
        }
      }
    }
  }
  return out.filter((f) => f.dueOn >= from && f.dueOn <= to).sort((a, b) => a.dueOn.localeCompare(b.dueOn));
}

/** Deposits and returns due after each pay date (semiweekly federal deposits, Maryland accelerated filers). */
export function perPayDateFilings(p: PayrollProfileRules, payDates: string[], holidays?: StateHolidays): FilingDue[] {
  const out: FilingDue[] = [];
  for (const r of PAYROLL_RULES) {
    if (!r.perPayDate || !r.applies(p)) continue;
    for (const d of payDates)
      out.push({ ruleKey: r.key, period: d, periodLabel: `Pay date ${shortDate(d)}`, dueOn: perPayDue(r, d, holidays) });
  }
  return out;
}

/**
 * A per-pay-date due date. Maryland accelerated: 3 business days, not counting the firm's Maryland
 * holidays. VA-15: the federal semiweekly date, moved past a Virginia holiday the firm added.
 */
function perPayDue(r: PayrollRule, d: string, holidays?: StateHolidays): string {
  if (r.perPayDate === "semiweekly") return semiweeklyDepositDate(d);
  if (r.perPayDate === "va_semiweekly") {
    const fed = semiweeklyDepositDate(d);
    const extra = stateExtra(r.jurisdiction, fed, holidays);
    return extra?.length ? nextBusinessDay(fed, { irs: false, extraHolidays: extra }) : fed;
  }
  const extra = new Set(stateExtra(r.jurisdiction, d, holidays) ?? []);
  let out = d;
  for (let i = 0; i < 3; i++) {
    out = addBusinessDays(out, 1);
    while (extra.has(out)) out = addBusinessDays(out, 1);
  }
  return out;
}

// --- State withholding frequency over time ----------------------------------------------------

export type WithholdingState = "MD" | "VA" | "DC";
export const WITHHOLDING_STATES: WithholdingState[] = ["MD", "VA", "DC"];
export const WITHHOLDING_FREQUENCIES = {
  MD: ["accelerated", "monthly", "quarterly", "annual"],
  VA: ["semiweekly", "monthly", "quarterly"],
  DC: ["monthly", "quarterly", "annual"],
} as const satisfies Record<WithholdingState, readonly string[]>;
const FREQUENCY_FIELD = { MD: "mdWithholdingFrequency", VA: "vaWithholdingFrequency", DC: "dcWithholdingFrequency" } as const;

/**
 * One state's withholding frequency from a date (`effectiveFrom`, inclusive; null means since the firm
 * took on the client's payroll). A client keeps its history: the frequency before a change still
 * governs the periods and pay dates before the change's date.
 */
export type FrequencyStep = { state: WithholdingState; frequency: string; effectiveFrom: string | null };

/**
 * What each state says about when a new filing frequency starts, shown next to the date. Virginia's
 * Employer Withholding Instructions: filing status changes are effective on January 1 of each year,
 * and Virginia writes to the employer. Maryland's and DC's guides could not be checked for a stated
 * effective date, so their notes are neutral: use the date the agency gave.
 */
export const FREQUENCY_CHANGE_NOTES: Record<WithholdingState, string> = {
  VA: "Virginia changes filing frequency on January 1 unless its letter says otherwise. Enter the date from Virginia's letter.",
  MD: "Enter the date the Comptroller of Maryland's notice gives for the new filing frequency.",
  DC: "Enter the date DC's Office of Tax and Revenue gave for the new filing frequency.",
};

/** The January 1 after `today` (the usual date a new state filing frequency starts). */
export function nextJanuary1(today: string): string {
  return `${Number(today.slice(0, 4)) + 1}-01-01`;
}

/** The first and last day of a filing period: a pay date, month (YYYY-MM), quarter (YYYY-Qn) or year. */
export function filingPeriodStart(period: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) return period;
  const q = /^(\d{4})-Q(\d)$/.exec(period);
  if (q) return `${q[1]}-${String((Number(q[2]) - 1) * 3 + 1).padStart(2, "0")}-01`;
  if (/^\d{4}-\d{2}$/.test(period)) return `${period}-01`;
  return `${period}-01-01`;
}
export function filingPeriodEnd(period: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) return period;
  const q = /^(\d{4})-Q(\d)$/.exec(period);
  const [y, m] = q ? [Number(q[1]), Number(q[2]) * 3] : /^\d{4}-\d{2}$/.test(period) ? [Number(period.slice(0, 4)), Number(period.slice(5))] : [Number(period), 12];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/** The profile's rules with each state's withholding frequency in effect on `date`. */
export function rulesOn(p: PayrollProfileRules, steps: FrequencyStep[], date: string): PayrollProfileRules {
  const out = { ...p };
  for (const st of WITHHOLDING_STATES) {
    const mine = steps.filter((x) => x.state === st && (x.effectiveFrom === null || x.effectiveFrom <= date)).sort((a, b) => (a.effectiveFrom ?? "").localeCompare(b.effectiveFrom ?? ""));
    const last = mine.at(-1);
    if (last) (out as Record<string, unknown>)[FREQUENCY_FIELD[st]] = last.frequency;
  }
  return out;
}

/** Stretches of time with one set of frequencies: [start, end] inclusive, null for open-ended. */
function frequencySpans(steps: FrequencyStep[]): Array<{ start: string | null; end: string | null }> {
  const breaks = [...new Set(steps.map((x) => x.effectiveFrom).filter((x): x is string => !!x))].sort();
  const spans: Array<{ start: string | null; end: string | null }> = [];
  let start: string | null = null;
  for (const b of breaks) {
    spans.push({ start, end: addDays(b, -1) });
    start = b;
  }
  spans.push({ start, end: null });
  return spans;
}
const overlaps = (period: string, span: { start: string | null; end: string | null }) => filingPeriodEnd(period) >= (span.start ?? "") && filingPeriodStart(period) <= (span.end ?? "9999-12-31");

/** Whether a rule's being owed depends on a state's withholding frequency (VA-5 monthly, VA-15, MW506 accelerated...). */
export function dependsOnFrequency(r: PayrollRule): boolean {
  if (r.jurisdiction === "federal") return false;
  const st = r.jurisdiction;
  const base: PayrollProfileRules = { states: [st], federalDepositSchedule: "monthly", files944: false, mdWithholdingFrequency: "quarterly", vaWithholdingFrequency: "quarterly", dcWithholdingFrequency: "quarterly", employeeCount: 1, hasContractors: false };
  const seen = new Set(WITHHOLDING_FREQUENCIES[st].map((f) => r.applies({ ...base, [FREQUENCY_FIELD[st]]: f } as PayrollProfileRules)));
  return seen.size > 1;
}

/**
 * Every calendar filing due in [from, to] (as filingsDue), with each state's frequency taken from the
 * client's history: a monthly VA-5 is owed for months that begin before the change to semiweekly, and
 * a VA-16 for quarters that end on or after it.
 */
export function filingsDueOverTime(p: PayrollProfileRules, steps: FrequencyStep[], from: string, to: string, holidays?: StateHolidays): FilingDue[] {
  const seen = new Set<string>();
  const out: FilingDue[] = [];
  for (const span of frequencySpans(steps))
    for (const f of filingsDue(rulesOn(p, steps, span.start ?? "0000-01-01"), from, to, holidays)) {
      const k = `${f.ruleKey}|${f.period}`;
      if (seen.has(k) || !overlaps(f.period, span)) continue;
      seen.add(k);
      out.push(f);
    }
  return out.sort((a, b) => a.dueOn.localeCompare(b.dueOn));
}

/** Deposits and returns after each pay date, under the frequency in effect on that pay date. */
export function perPayDateFilingsOverTime(p: PayrollProfileRules, steps: FrequencyStep[], payDates: string[], holidays?: StateHolidays): FilingDue[] {
  return payDates.flatMap((d) => perPayDateFilings(rulesOn(p, steps, d), [d], holidays));
}

/** Whether a filing is owed under the frequencies in effect during its period (or on its pay date). */
export function owedOverTime(p: PayrollProfileRules, steps: FrequencyStep[], ruleKey: string, period: string): boolean {
  const r = PAYROLL_RULES.find((x) => x.key === ruleKey);
  if (!r) return true;
  if (r.perPayDate || r.fromLiabilities) return r.applies(rulesOn(p, steps, period));
  return frequencySpans(steps).some((span) => overlaps(period, span) && r.applies(rulesOn(p, steps, span.start ?? "0000-01-01")));
}

/**
 * The part of a frequency-dependent filing's period it covers when the frequency changed inside the
 * period ("pay dates Oct 1 to Oct 22"), or null when it covers the whole period.
 */
export function coveredPart(p: PayrollProfileRules, steps: FrequencyStep[], ruleKey: string, period: string): { from: string; through: string } | null {
  const r = PAYROLL_RULES.find((x) => x.key === ruleKey);
  if (!r || r.perPayDate || r.fromLiabilities || !dependsOnFrequency(r)) return null;
  const ps = filingPeriodStart(period);
  const pe = filingPeriodEnd(period);
  let from: string | null = null;
  let through: string | null = null;
  for (const span of frequencySpans(steps)) {
    if (!overlaps(period, span) || !r.applies(rulesOn(p, steps, span.start ?? "0000-01-01"))) continue;
    const a = span.start && span.start > ps ? span.start : ps;
    const b = span.end && span.end < pe ? span.end : pe;
    if (!from || a < from) from = a;
    if (!through || b > through) through = b;
  }
  if (!from || !through || (from === ps && through === pe)) return null;
  return { from, through };
}

/** "Aug 21, 2026": dates as people read them, for labels built here. */
export function shortDate(iso: string): string {
  return at(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/**
 * Semiweekly deposit date (Publication 15): pay dates Wednesday–Friday end a period on Friday,
 * Saturday–Tuesday on Tuesday; the deposit is due 3 weekdays later, plus a business day for each
 * legal holiday among those 3 weekdays.
 */
export function semiweeklyDepositDate(payDate: string): string {
  let d = semiweeklyPeriodEnd(payDate);
  let holidays = 0;
  for (let n = 0; n < 3; ) {
    d = addDays(d, 1);
    const wd = at(d).getUTCDay();
    if (wd === 0 || wd === 6) continue;
    n++;
    if (federalHolidays(Number(d.slice(0, 4)), { irs: false }).has(d)) holidays++;
  }
  // d is the 3rd weekday; each holiday among the three adds a business day (a holiday on the 3rd
  // weekday itself is counted once, not rolled past and then added again).
  return holidays ? addBusinessDays(d, holidays) : d;
}

/** The last day of a payday's semiweekly period: Friday for Wednesday–Friday, Tuesday for Saturday–Tuesday. */
export function semiweeklyPeriodEnd(payDate: string): string {
  const w = at(payDate).getUTCDay();
  return w >= 3 && w <= 5 ? addDays(payDate, 5 - w) : addDays(payDate, (2 - w + 7) % 7);
}

// --- Deposits from recorded amounts (IRS Publication 15, section 11; Form 940 instructions) -------

/** Dollar thresholds, in cents. */
export const DEPOSIT_RULES = {
  /** $100,000 accumulated in a deposit period: deposit by the next business day. */
  nextDayCents: 10_000_000,
  /** Under $2,500 for the quarter (or the prior quarter; for Form 944, the year): pay with the return. */
  payWithReturnCents: 250_000,
  /** FUTA not yet deposited above $500: deposit by the last day of the month after the quarter. */
  futaDepositCents: 50_000,
  /** $50,000 or less in the lookback period: monthly depositor; more: semiweekly. */
  lookbackMonthlyMaxCents: 5_000_000,
} as const;

/**
 * One payroll as the deposit rules see it. federalTaxCents is federal income tax withheld plus
 * employee and employer Social Security and Medicare; futaCents is the FUTA tax. Amounts are null
 * until recorded. A skipped payroll counts as zero.
 */
export type LiabilityRun = { payDate: string; state: "processed" | "skipped" | "open"; federalTaxCents: number | null; futaCents: number | null };
/**
 * What ClientFlow can vouch for: payrolls from trackedFrom on (when the firm took the client's
 * payroll on), periods that ended before today, and every expected pay date present as a payroll.
 */
export type LiabilityCoverage = { trackedFrom: string; today: string; expectedPayDates?: string[] };

const quarterOf = (d: string) => `${d.slice(0, 4)}-Q${Math.floor((Number(d.slice(5, 7)) - 1) / 3) + 1}`;
function quarterBounds(q: string): [string, string] {
  const y = Number(q.slice(0, 4));
  const n = Number(q.slice(6));
  return [`${y}-${String(n * 3 - 2).padStart(2, "0")}-01`, iso(new Date(Date.UTC(y, n * 3, 0)))];
}
const prevQuarter = (q: string) => (q.endsWith("Q1") ? `${Number(q.slice(0, 4)) - 1}-Q4` : `${q.slice(0, 6)}${Number(q.slice(6)) - 1}`);
const nextQuarter = (q: string) => (q.endsWith("Q4") ? `${Number(q.slice(0, 4)) + 1}-Q1` : `${q.slice(0, 6)}${Number(q.slice(6)) + 1}`);

/** The total of one amount over [start, end], or null when ClientFlow can't vouch for every payroll in it. */
export function knownTotal(runs: LiabilityRun[], cov: LiabilityCoverage, start: string, end: string, field: "federalTaxCents" | "futaCents"): number | null {
  if (start < cov.trackedFrom || end >= cov.today) return null;
  const have = new Set(runs.map((r) => r.payDate));
  if (cov.expectedPayDates?.some((d) => d >= start && d <= end && !have.has(d))) return null;
  let total = 0;
  for (const r of runs) {
    if (r.payDate < start || r.payDate > end || r.state === "skipped") continue;
    if (r.state !== "processed" || r[field] === null) return null;
    total += r[field]!;
  }
  return total;
}

/**
 * Next-day deposits: federal taxes on the payrolls of one deposit period (the month for a monthly
 * depositor, the Wednesday–Friday or Saturday–Tuesday period for a semiweekly one) reaching
 * $100,000 are due the next business day, counted from the pay date. The count starts again after
 * each next-day deposit, and from then on the employer is semiweekly for the rest of the year and
 * the next, so later periods are semiweekly ones.
 */
export function nextDayDeposits(p: PayrollProfileRules, runs: LiabilityRun[]): Array<FilingDue & { amountCents: number }> {
  const byDay = new Map<string, number>();
  for (const r of runs) if (r.state === "processed" && r.federalTaxCents) byDay.set(r.payDate, (byDay.get(r.payDate) ?? 0) + r.federalTaxCents);
  const out: Array<FilingDue & { amountCents: number }> = [];
  let period = "";
  let acc = 0;
  let semiweeklyFrom: string | null = null;
  for (const d of [...byDay.keys()].sort()) {
    const sw = p.federalDepositSchedule === "semiweekly" || (semiweeklyFrom !== null && d >= semiweeklyFrom && Number(d.slice(0, 4)) <= Number(semiweeklyFrom.slice(0, 4)) + 1);
    const per = sw ? `sw ${semiweeklyPeriodEnd(d)}` : `m ${d.slice(0, 7)}`;
    if (per !== period) {
      period = per;
      acc = 0;
    }
    acc += byDay.get(d)!;
    if (acc >= DEPOSIT_RULES.nextDayCents) {
      out.push({ ruleKey: "fed_deposit_nextday", period: d, periodLabel: `Pay date ${shortDate(d)}`, dueOn: addBusinessDays(d, 1), amountCents: acc });
      acc = 0;
      semiweeklyFrom ??= addDays(d, 1);
    }
  }
  return out;
}

/**
 * When a next-day deposit makes the employer semiweekly (Publication 15: from the next day for the
 * rest of that year and all of the next), whatever the profile's setting says: [from, through]
 * windows, merged. Empty for a semiweekly profile (its calendar already is semiweekly).
 */
export function semiweeklyWindows(p: PayrollProfileRules, runs: LiabilityRun[]): Array<{ from: string; through: string; nextDayOn: string }> {
  if (p.federalDepositSchedule === "semiweekly") return [];
  const out: Array<{ from: string; through: string; nextDayOn: string }> = [];
  for (const f of nextDayDeposits(p, runs)) {
    const from = addDays(f.period, 1);
    const through = `${Number(f.period.slice(0, 4)) + 1}-12-31`;
    const last = out.at(-1);
    if (last && from <= addDays(last.through, 1)) last.through = through > last.through ? through : last.through;
    else out.push({ from, through, nextDayOn: f.period });
  }
  return out;
}

/**
 * Periods whose federal deposits may be paid with the return instead (the $2,500 rule): for a
 * Form 941 filer, a quarter whose taxes, or the prior quarter's, were under $2,500, with no
 * next-day deposit in it; for a Form 944 filer, a year under $2,500. Only periods ClientFlow can
 * vouch for are listed; anything it can't is left to be deposited.
 */
export function payWithReturnPeriods(p: PayrollProfileRules, runs: LiabilityRun[], cov: LiabilityCoverage): Array<{ period: string; totalCents: number; basis: "quarter" | "prior_quarter" | "year" }> {
  const nextDay = nextDayDeposits(p, runs).map((f) => f.period);
  const out: Array<{ period: string; totalCents: number; basis: "quarter" | "prior_quarter" | "year" }> = [];
  if (p.files944) {
    for (let y = Number(cov.trackedFrom.slice(0, 4)); y <= Number(cov.today.slice(0, 4)); y++) {
      const t = knownTotal(runs, cov, `${y}-01-01`, `${y}-12-31`, "federalTaxCents");
      if (t !== null && t < DEPOSIT_RULES.payWithReturnCents && !nextDay.some((d) => d.startsWith(String(y)))) out.push({ period: String(y), totalCents: t, basis: "year" });
    }
    return out;
  }
  for (let q = quarterOf(cov.trackedFrom); q <= quarterOf(cov.today); q = nextQuarter(q)) {
    const [a, b] = quarterBounds(q);
    if (nextDay.some((d) => d >= a && d <= b)) continue;
    const cur = knownTotal(runs, cov, a, b, "federalTaxCents");
    if (cur !== null && cur < DEPOSIT_RULES.payWithReturnCents) {
      out.push({ period: q, totalCents: cur, basis: "quarter" });
      continue;
    }
    const [pa, pb] = quarterBounds(prevQuarter(q));
    const prev = knownTotal(runs, cov, pa, pb, "federalTaxCents");
    if (prev !== null && prev < DEPOSIT_RULES.payWithReturnCents) out.push({ period: q, totalCents: prev, basis: "prior_quarter" });
  }
  return out;
}

/**
 * FUTA deposits by quarter (Form 940 instructions): FUTA not yet deposited this year above $500 is
 * deposited by the last day of the month after the quarter; $500 or less carries to the next
 * quarter, and a fourth-quarter balance of $500 or less is paid with Form 940. Only years whose
 * every payroll ClientFlow can vouch for, from January 1, are worked out; a quarter it can't vouch
 * for ends that year's count.
 */
export function futaDeposits(runs: LiabilityRun[], cov: LiabilityCoverage): Array<{ period: string; amountCents: number; deposit: boolean }> {
  const out: Array<{ period: string; amountCents: number; deposit: boolean }> = [];
  for (let y = Number(cov.trackedFrom.slice(0, 4)); y <= Number(cov.today.slice(0, 4)); y++) {
    let owed = 0;
    for (let q = 1; q <= 4; q++) {
      const [a, b] = quarterBounds(`${y}-Q${q}`);
      const t = knownTotal(runs, cov, a, b, "futaCents");
      if (t === null) break;
      owed += t;
      const deposit = owed > DEPOSIT_RULES.futaDepositCents;
      out.push({ period: `${y}-Q${q}`, amountCents: owed, deposit });
      if (deposit) owed = 0;
    }
  }
  return out;
}

/**
 * The federal deposit schedule for a year from what ClientFlow holds: semiweekly after a next-day
 * deposit this year or last; otherwise from the lookback period's taxes (Form 941: July 1 two years
 * back to June 30 last year; Form 944: the calendar year two years back) when every payroll in it is
 * recorded; otherwise no suggestion.
 */
export function depositScheduleCheck(p: PayrollProfileRules, runs: LiabilityRun[], cov: LiabilityCoverage, year = Number(cov.today.slice(0, 4))) {
  const [lookbackFrom, lookbackTo] = p.files944 ? [`${year - 2}-01-01`, `${year - 2}-12-31`] : [`${year - 2}-07-01`, `${year - 1}-06-30`];
  const lookbackTotalCents = knownTotal(runs, cov, lookbackFrom, lookbackTo, "federalTaxCents");
  const nextDayOn = nextDayDeposits(p, runs).map((f) => f.period).filter((d) => Number(d.slice(0, 4)) >= year - 1 && Number(d.slice(0, 4)) <= year).at(-1) ?? null;
  const suggested: "monthly" | "semiweekly" | null = nextDayOn ? "semiweekly" : lookbackTotalCents === null ? null : lookbackTotalCents <= DEPOSIT_RULES.lookbackMonthlyMaxCents ? "monthly" : "semiweekly";
  return { year, lookbackFrom, lookbackTo, lookbackTotalCents, nextDayOn, suggested };
}

export const PAYROLL_CHANGE_KINDS: Record<string, string> = {
  new_hire: "New hire",
  termination: "Termination",
  pay_change: "Pay change (raise, rate, hours)",
  address: "Address change",
  withholding: "Tax withholding change (W-4)",
  bank: "Direct deposit change",
  bonus: "Bonus or one-time payment",
  other: "Other",
};
